import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { XMarkIcon, PhotoIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from 'react-query';
import api from '../../../services/api';  
import { toast } from 'react-toastify';
import TourVehicleAssignment from '../../../components/Admin/Tours/TourVehicleAssignment';

// Empty arrays for locations and categories if API fails
const EMPTY_LOCATIONS = [];
const EMPTY_CATEGORIES = [];

const TourForm = ({ tour, onSubmit, onCancel, onImagePreview, id, refetch }) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  
  const queryClient = useQueryClient();
  
  // Preload locations and categories data
  useEffect(() => {
    // Immediately prefetch locations and categories
    const prefetchData = async () => {
      try {
        console.log('Prefetching locations and categories data...');
        
        // Prefetch locations
        queryClient.prefetchQuery('tourFormLocations', async () => {
          const response = await api.get('/api/v1/admin/locations', {
            headers: { 'Cache-Control': 'no-cache' }
          });
          return response.data;
        });
        
        // Prefetch categories
        queryClient.prefetchQuery('tourFormCategories', async () => {
          const response = await api.get('/api/v1/admin/categories', {
            headers: { 'Cache-Control': 'no-cache' }
          });
          return response.data;
        });
        
        console.log('Prefetching complete');
      } catch (error) {
        console.error('Error prefetching data:', error);
      }
    };
    
    prefetchData();
  }, [queryClient]);
  
  // Initialize array fields with at least one empty item for better UX
  const [includedServices, setIncludedServices] = useState(
    Array.isArray(tour?.inclusions) && tour.inclusions.length > 0 
      ? tour.inclusions 
      : ['']
  );
  
  const [excludedServices, setExcludedServices] = useState(
    Array.isArray(tour?.exclusions) && tour.exclusions.length > 0 
      ? tour.exclusions 
      : ['']
  );
  
  const [itineraryItems, setItineraryItems] = useState(
    Array.isArray(tour?.itinerary) && tour.itinerary.length > 0 
      ? tour.itinerary 
      : ['']
  );

  // Fetch locations for dropdown with optimized loading
  const { data: locations, isLoading: locationsLoading, error: locationsError } = useQuery('tourFormLocations', async () => {
    try {      
      console.log('Fetching locations for tour form...');
      // Force non-paginated response for dropdowns
      const response = await api.get('/api/v1/admin/locations', {
        headers: { 'Cache-Control': 'no-cache' },
        timeout: 5000 // 5 second timeout
      });
      
      // Simplified response handling - directly use the array if it's an array
      if (Array.isArray(response.data)) {
        console.log(`Found ${response.data.length} locations directly in array`);
        return response.data.length > 0 ? response.data : EMPTY_LOCATIONS;
      }
      // Handle standard success response format
      else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        console.log(`Found ${response.data.data.length} locations in success response`);
        return response.data.data.length > 0 ? response.data.data : EMPTY_LOCATIONS;
      }
      // Handle paginated response
      else if (response.data && response.data.data && Array.isArray(response.data.data.data)) {
        console.log(`Found ${response.data.data.data.length} locations in paginated response`);
        return response.data.data.data.length > 0 ? response.data.data.data : EMPTY_LOCATIONS;
      }
      
      console.warn('API returned unexpected locations data format:', response.data);
      return EMPTY_LOCATIONS;
    } catch (error) {
      console.error('Error fetching locations:', error);
      return EMPTY_LOCATIONS;
    }
  }, {
    staleTime: 30000, // 30 seconds
    cacheTime: 60000, // 1 minute
    refetchOnWindowFocus: true,
    refetchOnMount: false, // Don't refetch on mount since we're using preloaded data
    refetchInterval: 15000, // Refetch every 15 seconds
    retry: 2,
    retryDelay: 1000, // 1 second between retries
    initialData: () => {
      // Try to use preloaded data from the cache
      const preloadedData = queryClient.getQueryData('tourFormLocations');
      if (preloadedData) {
        console.log('Using preloaded locations data:', 
          Array.isArray(preloadedData) ? `${preloadedData.length} locations` : 'data in unknown format');
        return preloadedData;
      }
      return EMPTY_LOCATIONS;
    },
    suspense: false,
    useErrorBoundary: false
  });

  // Fetch categories for dropdown with optimized loading
  const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useQuery('tourFormCategories', async () => {
    try {
      console.log('Fetching categories for tour form...');
      // Force non-paginated response for dropdowns
      const response = await api.get('/api/v1/admin/categories', {
        headers: { 'Cache-Control': 'no-cache' },
        timeout: 5000 // 5 second timeout
      });
      
      // Simplified response handling - directly use the array if it's an array
      if (Array.isArray(response.data)) {
        console.log(`Found ${response.data.length} categories directly in array`);
        return response.data.length > 0 ? response.data : EMPTY_CATEGORIES;
      }
      // Handle standard success response format
      else if (response.data && response.data.success && Array.isArray(response.data.data)) {
        console.log(`Found ${response.data.data.length} categories in success response`);
        return response.data.data.length > 0 ? response.data.data : EMPTY_CATEGORIES;
      }
      // Handle paginated response
      else if (response.data && response.data.data && Array.isArray(response.data.data.data)) {
        console.log(`Found ${response.data.data.data.length} categories in paginated response`);
        return response.data.data.data.length > 0 ? response.data.data.data : EMPTY_CATEGORIES;
      }
      
      console.warn('API returned unexpected categories data format:', response.data);
      return EMPTY_CATEGORIES;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return EMPTY_CATEGORIES;
    }
  }, {
    staleTime: 30000, // 30 seconds
    cacheTime: 60000, // 1 minute
    refetchOnWindowFocus: true,
    refetchOnMount: false, // Don't refetch on mount since we're using preloaded data
    refetchInterval: 15000, // Refetch every 15 seconds
    retry: 2,
    retryDelay: 1000, // 1 second between retries
    initialData: () => {
      // Try to use preloaded data from the cache
      const preloadedData = queryClient.getQueryData('tourFormCategories');
      if (preloadedData) {
        console.log('Using preloaded categories data:', 
          Array.isArray(preloadedData) ? `${preloadedData.length} categories` : 'data in unknown format');
        return preloadedData;
      }
      return EMPTY_CATEGORIES;
    },
    suspense: false,
    useErrorBoundary: false
  });

  // No longer using useEffect hooks for locations and categories data
  // We'll use the query results directly in the form

  // Pass image preview to parent component
  useEffect(() => {
    if (onImagePreview && imagePreview) {
      onImagePreview(imagePreview);
    }
  }, [imagePreview, onImagePreview]);

  // Form validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required('Tour name is required').max(100, 'Tour name cannot exceed 100 characters'),
    description: Yup.string().required('Description is required').max(2000, 'Description cannot exceed 2000 characters'),
    price: Yup.number().required('Price is required').positive('Price must be positive').typeError('Price must be a number'),
    discount_price: Yup.number().positive('Discount price must be positive').typeError('Discount price must be a number'),
    duration: Yup.number().required('Duration is required').positive('Duration must be positive').integer('Duration must be a whole number').typeError('Duration must be a number'),
    location_id: Yup.number().required('Location is required'),
    duration_type: Yup.string().required('Duration type is required').oneOf(['hours', 'days', 'weeks'], 'Invalid duration type'),
    group_size: Yup.number().required('Group size is required').positive('Group size must be positive').integer('Group size must be a whole number').typeError('Group size must be a number'),
    is_featured: Yup.boolean(),
    is_active: Yup.boolean(),
    categories: Yup.array().min(1, 'At least one category is required'),
    included_services: Yup.array().of(Yup.string().required('Service is required')),
    excluded_services: Yup.array().of(Yup.string().required('Service is required')),
    itinerary: Yup.array().of(Yup.string().required('Itinerary item is required'))
  });

  // Initialize form with tour data or defaults
  const formik = useFormik({
    initialValues: {
      name: tour?.name || '',
      description: tour?.description || '',
      price: tour?.price || '',
      discount_price: tour?.discount_price || '',
      duration: tour?.duration || '',
      duration_type: tour?.duration_type || 'days',
      group_size: tour?.group_size || '',
      location_id: tour?.location_id || '',
      featured_image: null,
      gallery_images: [],
      categories: tour?.categories?.map(cat => cat.id) || [1], // Default to first category
      inclusions: includedServices,
      exclusions: excludedServices,
      itinerary: itineraryItems,
      is_featured: tour?.is_featured || false,
      is_active: tour?.is_active || true
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        console.log('Form submitted with values:', values);
        
        // Create FormData object
        const formData = new FormData();
        
        // Add basic fields
        formData.append('name', values.name);
        formData.append('description', values.description);
        formData.append('price', values.price);
        formData.append('discount_price', values.discount_price || '');
        formData.append('duration', values.duration);
        formData.append('duration_type', values.duration_type);
        formData.append('group_size', values.group_size);
        formData.append('location_id', values.location_id);
        formData.append('is_featured', values.is_featured ? '1' : '0');
        formData.append('is_active', values.is_active ? '1' : '0');
        
        // Add featured image if present
        if (values.featured_image) {
          formData.append('featured_image', values.featured_image);
        }
        
        // Add gallery images if present
        if (values.gallery_images && values.gallery_images.length > 0) {
          for (let i = 0; i < values.gallery_images.length; i++) {
            formData.append('gallery_images[]', values.gallery_images[i]);
          }
        }
        
        // Add categories
        if (values.categories && values.categories.length > 0) {
          values.categories.forEach((categoryId, index) => {
            formData.append(`categories[${index}]`, categoryId);
          });
        } else {
          // Default category if none selected
          formData.append('categories[0]', 1);
        }
        
        // Add array fields
        values.inclusions.forEach((item, index) => {
          if (item.trim()) {
            formData.append(`included_services[${index}]`, item);
          }
        });
        
        values.exclusions.forEach((item, index) => {
          if (item.trim()) {
            formData.append(`excluded_services[${index}]`, item);
          }
        });
        
        values.itinerary.forEach((item, index) => {
          if (item.trim()) {
            formData.append(`itinerary[${index}]`, item);
          }
        });
        
        // Debug log
        console.log('FormData entries:');
        for (let [key, value] of formData.entries()) {
          console.log(`${key}: ${value}`);
        }
        
        // Submit the form data
        console.log('Calling onSubmit with formData');
        onSubmit(formData);
      } catch (error) {
        console.error('Error preparing form data:', error);
        toast.error('Error preparing form data. Please try again.');
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Handle featured image change
  const handleFeaturedImageChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      formik.setFieldValue('featured_image', file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle gallery images change
  const handleGalleryImagesChange = (event) => {
    const files = event.currentTarget.files;
    if (files && files.length > 0) {
      formik.setFieldValue('gallery_images', files);
      
      const previews = [];
      for (let i = 0; i < files.length; i++) {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push(reader.result);
          if (previews.length === files.length) {
            setGalleryPreviews(previews);
          }
        };
        reader.readAsDataURL(files[i]);
      }
    }
  };

  // Set image previews if editing existing tour
  useEffect(() => {
    if (tour?.featured_image) {
      setImagePreview(tour.featured_image);
    }
    
    if (tour?.gallery_images && tour.gallery_images.length > 0) {
      setGalleryPreviews(tour.gallery_images);
    }
  }, [tour]);

  return (
    <div className="bg-white p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {tour ? 'Edit Tour' : 'Add New Tour'}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-500"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={formik.handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          {/* Tour Name */}
          <div className="sm:col-span-6">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Tour Name
            </label>
            <div className="mt-1">
              <input
                type="text"
                id="name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
              {formik.touched.name && formik.errors.name ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.name}</p>
              ) : null}
            </div>
          </div>

          {/* Description */}
          <div className="sm:col-span-6">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <div className="mt-1">
              <textarea
                id="description"
                name="description"
                rows={4}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
              {formik.touched.description && formik.errors.description ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.description}</p>
              ) : null}
            </div>
          </div>

          {/* Price */}
          <div className="sm:col-span-3">
            <label htmlFor="price" className="block text-sm font-medium text-gray-700">
              Price (USD)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="price"
                name="price"
                value={formik.values.price}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="focus:ring-primary focus:border-primary block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                placeholder="0.00"
              />
              {formik.touched.price && formik.errors.price ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.price}</p>
              ) : null}
            </div>
          </div>

          {/* Discount Price */}
          <div className="sm:col-span-3">
            <label htmlFor="discount_price" className="block text-sm font-medium text-gray-700">
              Discount Price (USD)
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                id="discount_price"
                name="discount_price"
                value={formik.values.discount_price}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="focus:ring-primary focus:border-primary block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                placeholder="0.00"
              />
              {formik.touched.discount_price && formik.errors.discount_price ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.discount_price}</p>
              ) : null}
            </div>
          </div>

          {/* Duration */}
          <div className="sm:col-span-3">
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
              Duration
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="duration"
                name="duration"
                value={formik.values.duration}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
              {formik.touched.duration && formik.errors.duration ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.duration}</p>
              ) : null}
            </div>
          </div>

          {/* Duration Type */}
          <div className="sm:col-span-3">
            <label htmlFor="duration_type" className="block text-sm font-medium text-gray-700">
              Duration Type
            </label>
            <div className="mt-1">
              <select
                id="duration_type"
                name="duration_type"
                value={formik.values.duration_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
                <option value="weeks">Weeks</option>
              </select>
              {formik.touched.duration_type && formik.errors.duration_type ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.duration_type}</p>
              ) : null}
            </div>
          </div>

          {/* Location */}
          <div className="sm:col-span-3">
            <label htmlFor="location_id" className="block text-sm font-medium text-gray-700">
              Location
            </label>
            <div className="mt-1 relative">
              {locationsLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
                  <div className="animate-pulse flex space-x-2">
                    <div className="h-2 w-2 bg-primary rounded-full"></div>
                    <div className="h-2 w-2 bg-primary rounded-full"></div>
                    <div className="h-2 w-2 bg-primary rounded-full"></div>
                  </div>
                </div>
              )}
              <select
                id="location_id"
                name="location_id"
                value={formik.values.location_id || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${locationsLoading ? 'opacity-50' : ''}`}
                disabled={locationsLoading}
              >
                <option value="">Select a location</option>
                {locations && locations.length > 0 ? (
                  locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}{location.region ? ` - ${location.region}` : ''}{location.country ? `, ${location.country}` : ''}
                    </option>
                  ))
                ) : (
                  <option disabled>No locations available</option>
                )}
              </select>
              {formik.touched.location_id && formik.errors.location_id ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.location_id}</p>
              ) : null}
              <div className="mt-1 flex items-center">
                {locationsLoading ? (
                  <p className="text-xs text-blue-600 flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading locations...
                  </p>
                ) : locations && locations.length > 0 ? (
                  <p className="text-xs text-green-600">{locations.length} locations available</p>
                ) : (
                  <p className="text-xs text-yellow-600">No locations found. Please create locations in the Locations section.</p>
                )}
              </div>
            </div>
          </div>

          {/* Group Size */}
          <div className="sm:col-span-3">
            <label htmlFor="group_size" className="block text-sm font-medium text-gray-700">
              Group Size
            </label>
            <div className="mt-1">
              <input
                type="number"
                id="group_size"
                name="group_size"
                value={formik.values.group_size}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              />
              {formik.touched.group_size && formik.errors.group_size ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.group_size}</p>
              ) : null}
            </div>
          </div>

          {/* Featured */}
          <div className="sm:col-span-3">
            <div className="flex items-center">
              <input
                id="is_featured"
                name="is_featured"
                type="checkbox"
                checked={formik.values.is_featured}
                onChange={formik.handleChange}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-900">
                Featured Tour
              </label>
            </div>
          </div>

          {/* Active */}
          <div className="sm:col-span-3">
            <div className="flex items-center">
              <input
                id="is_active"
                name="is_active"
                type="checkbox"
                checked={formik.values.is_active}
                onChange={formik.handleChange}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>
          </div>

          {/* Categories */}
          <div className="sm:col-span-6">
            <label htmlFor="categories" className="block text-sm font-medium text-gray-700">
              Categories
            </label>
            <div className="mt-1 relative">
              {categoriesLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
                  <div className="animate-pulse flex space-x-2">
                    <div className="h-2 w-2 bg-primary rounded-full"></div>
                    <div className="h-2 w-2 bg-primary rounded-full"></div>
                    <div className="h-2 w-2 bg-primary rounded-full"></div>
                  </div>
                </div>
              )}
              <select
                id="categories"
                name="categories"
                multiple
                value={formik.values.categories || []}
                onChange={(e) => {
                  const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                  formik.setFieldValue('categories', selectedOptions);
                }}
                onBlur={formik.handleBlur}
                className={`shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md ${categoriesLoading ? 'opacity-50' : ''}`}
                size={Math.min(5, categories && categories.length > 0 ? categories.length : 3)}
                disabled={categoriesLoading}
              >
                {categories && categories.length > 0 ? (
                  categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))
                ) : (
                  <option disabled>No categories available</option>
                )}
              </select>
              <p className="mt-1 text-sm text-gray-500">Hold Ctrl/Cmd to select multiple categories</p>
              {formik.touched.categories && formik.errors.categories ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.categories}</p>
              ) : null}
              <div className="mt-1 flex items-center">
                {categoriesLoading ? (
                  <p className="text-xs text-blue-600 flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Loading categories...
                  </p>
                ) : categories && categories.length > 0 ? (
                  <p className="text-xs text-green-600">{categories.length} categories available</p>
                ) : (
                  <p className="text-xs text-yellow-600">No categories found. Please create categories in the Categories section.</p>
                )}
              </div>
            </div>
          </div>

          {/* Inclusions */}
          <div className="sm:col-span-6">
            <label htmlFor="inclusions" className="block text-sm font-medium text-gray-700">
              Inclusions
            </label>
            <div className="mt-1">
              {includedServices.map((service, index) => (
                <div key={index} className="flex items-center mb-2">
                  <input
                    type="text"
                    id={`inclusions_${index}`}
                    name={`inclusions.${index}`}
                    value={service}
                    onChange={(e) => {
                      const newServices = [...includedServices];
                      newServices[index] = e.target.value;
                      setIncludedServices(newServices);
                      formik.setFieldValue('inclusions', newServices);
                    }}
                    onBlur={formik.handleBlur}
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setIncludedServices(includedServices.filter((s, i) => i !== index));
                      formik.setFieldValue('inclusions', includedServices.filter((s, i) => i !== index));
                    }}
                    className="ml-2 text-sm text-red-600 hover:text-red-900"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setIncludedServices([...includedServices, '']);
                  formik.setFieldValue('inclusions', [...includedServices, '']);
                }}
                className="text-sm text-primary hover:text-primary-dark"
              >
                Add Service
              </button>
              {formik.touched.inclusions && formik.errors.inclusions ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.inclusions}</p>
              ) : null}
            </div>
          </div>

          {/* Exclusions */}
          <div className="sm:col-span-6">
            <label htmlFor="exclusions" className="block text-sm font-medium text-gray-700">
              Exclusions
            </label>
            <div className="mt-1">
              {excludedServices.map((service, index) => (
                <div key={index} className="flex items-center mb-2">
                  <input
                    type="text"
                    id={`exclusions_${index}`}
                    name={`exclusions.${index}`}
                    value={service}
                    onChange={(e) => {
                      const newServices = [...excludedServices];
                      newServices[index] = e.target.value;
                      setExcludedServices(newServices);
                      formik.setFieldValue('exclusions', newServices);
                    }}
                    onBlur={formik.handleBlur}
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setExcludedServices(excludedServices.filter((s, i) => i !== index));
                      formik.setFieldValue('exclusions', excludedServices.filter((s, i) => i !== index));
                    }}
                    className="ml-2 text-sm text-red-600 hover:text-red-900"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setExcludedServices([...excludedServices, '']);
                  formik.setFieldValue('exclusions', [...excludedServices, '']);
                }}
                className="text-sm text-primary hover:text-primary-dark"
              >
                Add Service
              </button>
              {formik.touched.exclusions && formik.errors.exclusions ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.exclusions}</p>
              ) : null}
            </div>
          </div>

          {/* Itinerary */}
          <div className="sm:col-span-6">
            <label htmlFor="itinerary" className="block text-sm font-medium text-gray-700">
              Itinerary
            </label>
            <div className="mt-1">
              {itineraryItems.map((item, index) => (
                <div key={index} className="flex items-center mb-2">
                  <input
                    type="text"
                    id={`itinerary_${index}`}
                    name={`itinerary.${index}`}
                    value={item}
                    onChange={(e) => {
                      const newItems = [...itineraryItems];
                      newItems[index] = e.target.value;
                      setItineraryItems(newItems);
                      formik.setFieldValue('itinerary', newItems);
                    }}
                    onBlur={formik.handleBlur}
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setItineraryItems(itineraryItems.filter((i, idx) => idx !== index));
                      formik.setFieldValue('itinerary', itineraryItems.filter((i, idx) => idx !== index));
                    }}
                    className="ml-2 text-sm text-red-600 hover:text-red-900"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  setItineraryItems([...itineraryItems, '']);
                  formik.setFieldValue('itinerary', [...itineraryItems, '']);
                }}
                className="text-sm text-primary hover:text-primary-dark"
              >
                Add Item
              </button>
              {formik.touched.itinerary && formik.errors.itinerary ? (
                <p className="mt-2 text-sm text-red-600">{formik.errors.itinerary}</p>
              ) : null}
            </div>
          </div>

          {/* Featured Image Upload */}
          <div className="sm:col-span-6">
            <label className="block text-sm font-medium text-gray-700">Featured Image</label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                {imagePreview ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={imagePreview}
                      alt="Tour preview"
                      className="h-40 w-auto object-cover mb-4"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        formik.setFieldValue('featured_image', null);
                      }}
                      className="text-sm text-red-600 hover:text-red-900"
                    >
                      Remove image
                    </button>
                  </div>
                ) : (
                  <>
                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="featured_image"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                      >
                        <span>Upload a file</span>
                        <input
                          id="featured_image"
                          name="featured_image"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleFeaturedImageChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </>
                )}
              </div>
            </div>
            {formik.touched.featured_image && formik.errors.featured_image ? (
              <p className="mt-2 text-sm text-red-600">{formik.errors.featured_image}</p>
            ) : null}
          </div>

          {/* Gallery Images Upload */}
          <div className="sm:col-span-6">
            <label className="block text-sm font-medium text-gray-700">Gallery Images</label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                {galleryPreviews.length > 0 ? (
                  <div>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {galleryPreviews.map((preview, index) => (
                        <div key={index} className="relative">
                          <img
                            src={preview}
                            alt={`Gallery image ${index + 1}`}
                            className="h-24 w-full object-cover rounded"
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setGalleryPreviews([]);
                        formik.setFieldValue('gallery_images', []);
                      }}
                      className="text-sm text-red-600 hover:text-red-900"
                    >
                      Remove all images
                    </button>
                  </div>
                ) : (
                  <>
                    <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="gallery_images"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                      >
                        <span>Upload files</span>
                        <input
                          id="gallery_images"
                          name="gallery_images"
                          type="file"
                          accept="image/*"
                          multiple
                          className="sr-only"
                          onChange={handleGalleryImagesChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Add vehicle assignment section for edit mode */}
          {id && (
            <div className="mt-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Vehicle Assignment
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Assign vehicles to this tour and set a default vehicle.
              </p>
              <TourVehicleAssignment 
                tourId={id} 
                onUpdate={() => {
                  // Refresh tour data after vehicle assignment changes
                  if (refetch) {
                    refetch();
                  }
                }} 
              />
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={formik.isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {formik.isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {tour ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              tour ? 'Update Tour' : 'Create Tour'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

TourForm.propTypes = {
  tour: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onImagePreview: PropTypes.func,
  id: PropTypes.number,
  refetch: PropTypes.func
};

export default TourForm;
